import React from 'react';
import { Link } from 'react-router-dom';
import ServicesSubmenu from '../../components/ServicesSubmenu';
import '../../styles/ServiceDetail.css';

const ResponsiveDesign: React.FC = () => {
  return (
    <div className="service-detail-page">
      <section className="service-detail-hero">
        <div className="container">
          <h1>Responsive Web Design</h1>
          <p>Creating Beautiful, Mobile-First Websites That Work Everywhere</p>
        </div>
      </section>

      <ServicesSubmenu />

      <section className="service-overview">
        <div className="container">
          <div className="overview-content">
            <h2>Responsive Design for the Modern Web</h2>
            <p>In today's multi-device world, having a responsive website is crucial. We create stunning, mobile-first designs that provide an optimal viewing experience across all devices - from smartphones to desktop monitors.</p>
            <div className="key-features">
              <div className="feature-item">
                <i className="fas fa-mobile-alt"></i>
                <h3>Mobile-First</h3>
                <p>Optimized for smartphones and tablets</p>
              </div>
              <div className="feature-item">
                <i className="fas fa-desktop"></i>
                <h3>Cross-Device</h3>
                <p>Consistent experience across all screens</p>
              </div>
              <div className="feature-item">
                <i className="fas fa-bolt"></i>
                <h3>Fast Loading</h3>
                <p>Optimized for quick loading on all devices</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="service-details">
        <div className="container">
          <div className="details-grid">
            <div className="detail-item">
              <h3>Mobile-First Design</h3>
              <ul>
                <li>Touch-friendly navigation</li>
                <li>Optimized content layout</li>
                <li>Responsive images and media</li>
                <li>Mobile performance optimization</li>
              </ul>
            </div>
            <div className="detail-item">
              <h3>Cross-Browser Compatibility</h3>
              <ul>
                <li>Support for all modern browsers</li>
                <li>Graceful degradation for older browsers</li>
                <li>Consistent rendering across platforms</li>
                <li>Browser-specific optimizations</li>
              </ul>
            </div>
            <div className="detail-item">
              <h3>Performance Optimization</h3>
              <ul>
                <li>Fast loading times</li>
                <li>Optimized images and assets</li>
                <li>Efficient code structure</li>
                <li>Bandwidth consideration</li>
              </ul>
            </div>
            <div className="detail-item">
              <h3>User Experience Design</h3>
              <ul>
                <li>Intuitive navigation</li>
                <li>Clear content hierarchy</li>
                <li>Accessible design patterns</li>
                <li>Interactive elements optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="design-process">
        <div className="container">
          <h2>Our Design Process</h2>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Research & Analysis</h3>
              <p>Understanding user behavior across devices</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Wireframing</h3>
              <p>Creating responsive layouts and structures</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Design</h3>
              <p>Developing mobile-first, responsive designs</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Development</h3>
              <p>Building with responsive frameworks and tools</p>
            </div>
            <div className="step">
              <div className="step-number">5</div>
              <h3>Testing</h3>
              <p>Cross-device and browser testing</p>
            </div>
          </div>
        </div>
      </section>

      <section className="responsive-showcase">
        <div className="container">
          <h2>Responsive Design Features</h2>
          <div className="feature-grid">
            <div className="showcase-item">
              <i className="fas fa-columns"></i>
              <h3>Fluid Grids</h3>
              <p>Flexible layouts that adapt to any screen size</p>
            </div>
            <div className="showcase-item">
              <i className="fas fa-image"></i>
              <h3>Flexible Images</h3>
              <p>Images that scale within their containing elements</p>
            </div>
            <div className="showcase-item">
              <i className="fas fa-tablet-alt"></i>
              <h3>Media Queries</h3>
              <p>Optimized layouts for different device capabilities</p>
            </div>
            <div className="showcase-item">
              <i className="fas fa-universal-access"></i>
              <h3>Accessibility</h3>
              <p>WCAG compliant designs for all users</p>
            </div>
          </div>
        </div>
      </section>

      <section className="technologies">
        <div className="container">
          <h2>Technologies & Tools</h2>
          <div className="tech-stack">
            <div className="tech-category">
              <h3>Design Tools</h3>
              <ul>
                <li><i className="fab fa-figma"></i> Figma</li>
                <li><i className="fab fa-sketch"></i> Sketch</li>
                <li><i className="fab fa-adobe"></i> Adobe XD</li>
                <li><i className="fas fa-pencil-ruler"></i> Prototyping Tools</li>
              </ul>
            </div>
            <div className="tech-category">
              <h3>Frontend</h3>
              <ul>
                <li><i className="fab fa-html5"></i> HTML5</li>
                <li><i className="fab fa-css3-alt"></i> CSS3/SASS</li>
                <li><i className="fab fa-bootstrap"></i> Bootstrap</li>
                <li><i className="fab fa-js"></i> JavaScript</li>
              </ul>
            </div>
            <div className="tech-category">
              <h3>Testing Tools</h3>
              <ul>
                <li><i className="fas fa-mobile-alt"></i> Mobile Testing</li>
                <li><i className="fas fa-window-maximize"></i> Browser Testing</li>
                <li><i className="fas fa-tachometer-alt"></i> Performance Testing</li>
                <li><i className="fas fa-universal-access"></i> Accessibility Testing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="service-cta">
        <div className="container">
          <h2>Ready to Make Your Website Responsive?</h2>
          <p>Let's create a website that looks great on every device</p>
          <Link to="/contact" className="cta-button">Contact Us</Link>
        </div>
      </section>
    </div>
  );
};

export default ResponsiveDesign; 